import React, { useState, useEffect, useMemo } from 'react'
import { Alert, Col, Container, Form, Row } from 'react-bootstrap'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { ExternalUserServiceIntegrationConfig, ExternalUserServiceIntegrationUpdate } from '@zocks-communications/client-sdk-js'

import { Button } from '../../../../../components/Button/Button'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetUserActiveSessionsQuery } from '../../../../../store/media/media'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { zoomSettingsDefaultValues, ZoomSettingsFormValues } from '../../../../Integration/IntegrationManagement/IntegrationSpecificSettings/Zoom/models'
import { SelectDropdown, SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'

export const THREE_HOUR_IN_SECONDS = 3 * 60 * 60

export const ZoomSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)!

  const { data: userData, isLoading: isUserLoading } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [startedAfter] = useState(() => Math.floor(Date.now() / 1000) - THREE_HOUR_IN_SECONDS)
  const { data: activeSessions, isLoading: activeSessionLoading } = useGetUserActiveSessionsQuery(
    {
      accountId: accountId,
      userId: authenticatedUser.id,
      kind: 'external',
      startedAfter: startedAfter,
      pageRequest: { page: 0, size: 10 },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [updateUserIntegration, { isLoading: isUpdating }] = useUpdateUserIntegrationMutation()

  const form = useForm<ZoomSettingsFormValues>({
    defaultValues: zoomSettingsDefaultValues as ZoomSettingsFormValues,
  })
  const { handleSubmit, control, reset } = form

  const meetingInProgress = useMemo(() => {
    if (!activeSessionLoading && activeSessions) {
      return activeSessions.data.length > 0
    }
    return false
  }, [activeSessionLoading, activeSessions])

  useEffect(() => {
    if (userData && userData.integrations) {
      const integration = userData.integrations.find((i) => i.serviceId === IntegrationServiceId.ZOOM)

      if (integration) {
        const config = integration.configuration

        const removeSessionValue = config.type === 'recording' ? (config.removeSessionWhenNoRecording ?? true) : true

        reset({
          integrationType: config.type,
          autoJoin: config.type === 'bot' ? config.autoJoin : undefined,
          removeSessionWhenNoRecording: removeSessionValue,
        })
      }
    }
  }, [userData, reset])

  const handleSave = async (data: ZoomSettingsFormValues) => {
    const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)
    if (integration) {
      const { integrationType, removeSessionWhenNoRecording, autoJoin } = data
      const payload = {
        configuration: {
          type: integrationType,
          ...(integrationType === 'bot' && { autoJoin }),
          ...(integrationType === 'recording' && { removeSessionWhenNoRecording }),
        } as ExternalUserServiceIntegrationConfig,
      } as ExternalUserServiceIntegrationUpdate

      await updateUserIntegration({
        accountId,
        integrationName: integration.serviceId,
        integrationId: integration.id,
        payload: payload,
      })
    }
  }

  const watchedType = useWatch({ control, name: 'integrationType' })

  if (isUserLoading || !userData) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  const zoomSettingsSelectOptions: SelectOption[] = [
    { id: 'bot', label: 'AI Assistant' },
    { id: 'recording', label: 'Zoom Recording' },
  ]

  return (
    <Container className='p-0'>
      <Row className='mb-4'>
        <Col>
          <h3>Zoom Settings</h3>
          <p className='text-muted'>Manage how Zocks interacts with your Zoom meetings.</p>
          <p>
            Zocks can capture your Zoom meetings in two ways: with <b>AI Assistant</b>, it joins the Zoom meeting as a participant to listen and transcribe, or
            with <b>Zoom Recording</b>, Zoom notifies Zocks when the meeting is available so the media can be briefly accessed to create a transcript and then
            deleted. Zocks does not store any meeting content - Zoom's recording is used only to generate the transcript
          </p>
        </Col>
      </Row>

      {meetingInProgress && (
        <Row className='mb-4'>
          <Col>
            <Alert variant='warning'>You cannot change your Zoom settings while you have an active meeting in progress on Zoom.</Alert>
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit(handleSave)}>
        <Container className='d-flex flex-column gap-4 p-0'>
          <Row>
            <Col>
              <Controller
                name='integrationType'
                control={control}
                render={({ field, fieldState }) => {
                  const currentSelection = zoomSettingsSelectOptions.find((opt) => opt.id === field.value) || null

                  return (
                    <>
                      <Form.Group className='mb-3' controlId='integrationType'>
                        <Form.Label>Meeting Capture</Form.Label>
                        <SelectDropdown
                          options={zoomSettingsSelectOptions}
                          initialSelectedOption={currentSelection}
                          onOptionSelected={(option) => field.onChange(option.id)}
                          disabled={meetingInProgress}
                          placeholder='Select Integration Type'
                          className='w-100'
                        />
                      </Form.Group>
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </>
                  )
                }}
              />
            </Col>
          </Row>

          {watchedType === 'bot' && (
            <Row>
              <Col>
                <Controller
                  name='autoJoin'
                  control={control}
                  render={({ field, fieldState }) => (
                    <>
                      <Form.Group className='mb-3' controlId='botName'>
                        <Form.Check
                          checked={field.value === true}
                          onChange={field.onChange}
                          type='checkbox'
                          id='autoJoin'
                          label={`Auto-join my ad-hoc Zoom meetings`}
                          disabled={meetingInProgress}
                        />
                      </Form.Group>
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </>
                  )}
                />
              </Col>
            </Row>
          )}
          {watchedType === 'recording' && (
            <Row>
              <Col>
                <Controller
                  name='removeSessionWhenNoRecording'
                  control={control}
                  render={({ field, fieldState }) => (
                    <>
                      <Form.Group className='mb-3' controlId='removeSessionWhenNoRecording'>
                        <Form.Label>Recording</Form.Label>
                        <Form.Check
                          checked={field.value === true}
                          onChange={field.onChange}
                          type='checkbox'
                          id='removeSessionWhenNoRecordingCheckbox'
                          label={`Remove Meetings Without Recording`}
                          disabled={meetingInProgress}
                        />
                      </Form.Group>
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </>
                  )}
                />
              </Col>
            </Row>
          )}
          <Row className='mt-2'>
            <Col>
              <Button variant='primary' loading={isUpdating} disabled={meetingInProgress} onClick={handleSubmit(handleSave)}>
                Save Changes
              </Button>
            </Col>
          </Row>
        </Container>
      </Form>
    </Container>
  )
}
